import React, { useState, useEffect } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { documentsService, partnersService } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';

const DocumentUploadPage = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    partner_ids: 'all', // 'all' or comma-separated IDs
    category: 'general',
    is_public: false,
    send_notification: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const response = await partnersService.getAllPartners({ 
        status: 'approved',
        limit: 1000 
      });
      if (response.success) {
        setPartners(response.data || []);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // File size check (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error('Lütfen bir dosya seçin');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Başlık gereklidir');
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);

      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('title', formData.title);
      uploadFormData.append('description', formData.description || '');
      uploadFormData.append('partner_ids', formData.partner_ids);
      uploadFormData.append('category', formData.category);
      uploadFormData.append('is_public', formData.is_public ? 'true' : 'false');
      uploadFormData.append('send_notification', formData.send_notification ? 'true' : 'false');

      // Simulate progress (actual progress would need axios onUploadProgress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await documentsService.uploadDocument(uploadFormData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        toast.success(`Döküman başarıyla yüklendi (${response.data.partner_count} partner'e gönderildi)`);
        navigate('/documents');
      } else {
        toast.error(response.error || 'Döküman yüklenemedi');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Döküman yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Döküman Yükle" />
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Döküman Yükle</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Dosya Seçimi */}
            <div className="mb-4">
              <label className="form-label">Dosya <span className="text-danger">*</span></label>
              <input
                type="file"
                className="form-control"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                required
              />
              <small className="text-muted">
                Desteklenen formatlar: PDF, Word, Excel, Resim, Metin (Maksimum: 10MB)
              </small>
              {selectedFile && (
                <div className="mt-2">
                  <div className="alert alert-info mb-0">
                    <Icon icon="solar:file-outline" className="me-2" />
                    <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                </div>
              )}
            </div>

            {/* Başlık */}
            <div className="mb-4">
              <label className="form-label">Başlık <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                placeholder="Döküman başlığı"
              />
            </div>

            {/* Açıklama */}
            <div className="mb-4">
              <label className="form-label">Açıklama</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Döküman açıklaması (opsiyonel)"
              />
            </div>

            {/* Partner Seçimi */}
            <div className="mb-4">
              <label className="form-label">Partner Seçimi</label>
              <select
                className="form-select"
                value={formData.partner_ids}
                onChange={(e) => setFormData(prev => ({ ...prev, partner_ids: e.target.value }))}
              >
                <option value="all">Tüm Partnerler</option>
                {partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name} - {partner.phone}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Döküman seçilen partner'lere gönderilecektir
              </small>
            </div>

            {/* Kategori */}
            <div className="mb-4">
              <label className="form-label">Kategori</label>
              <select
                className="form-select"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="general">Genel</option>
                <option value="contract">Sözleşme</option>
                <option value="invoice">Fatura</option>
                <option value="report">Rapor</option>
                <option value="other">Diğer</option>
              </select>
            </div>

            {/* Seçenekler */}
            <div className="mb-4">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="is_public"
                  checked={formData.is_public}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="is_public">
                  Herkese açık
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="send_notification"
                  checked={formData.send_notification}
                  onChange={(e) => setFormData(prev => ({ ...prev, send_notification: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="send_notification">
                  Partner'lere bildirim gönder
                </label>
              </div>
            </div>

            {/* Upload Progress */}
            {loading && uploadProgress > 0 && (
              <div className="mb-4">
                <div className="progress">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                  >
                    {uploadProgress}%
                  </div>
                </div>
              </div>
            )}

            {/* Butonlar */}
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !selectedFile}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <Icon icon="solar:upload-outline" className="me-2" />
                    Yükle
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/documents')}
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      </div>
    </MasterLayout>
  );
};

export default DocumentUploadPage;

